import { TaskCategoryTaskType, TaskStepDto } from '@urbanx/agx.activities.client';
import { AgxBadge, AgxCheckbox, AgxTypography } from '@urbanx/agx.ui-components2';
import clsx from 'clsx';
import { getTaskStepDueDate } from 'utils/tasks';
import TaskDueDateBadge from './TaskDueDateBadge';
import { useMemo } from 'react';

const settlementTaskTypes = new Set([
  TaskCategoryTaskType.Settlement,
  TaskCategoryTaskType.SettlementPayments,
  TaskCategoryTaskType.SettlementPaymentsReview,
  TaskCategoryTaskType.NswSettlementPayments,
  TaskCategoryTaskType.VendorBankDetailsSettlement,
  TaskCategoryTaskType.FinaliseMarketingVpa,
  TaskCategoryTaskType.FinalisedMarketingPayments,
  TaskCategoryTaskType.FinalisedMarketingPaymentReview,
  TaskCategoryTaskType.NswFinalisedMarketingPaymentReview,
  TaskCategoryTaskType.NswFinalisedMarketingPayments,
]);

interface TaskStepItemProps {
  step: TaskStepDto;
  allStepsCompleted: boolean;
  isSaving?: boolean;
  taskType?: TaskCategoryTaskType;
  onToggleStep: (step: TaskStepDto) => void;
}

const TaskStepItem = ({
  step,
  allStepsCompleted,
  isSaving = false,
  taskType,
  onToggleStep,
}: TaskStepItemProps) => {
  const labelText = step.name ?? '';
  const helperText = step.data?.text;
  const badgeMessage = step.data?.badgeMessage?.trim();
  const stepDueDate = useMemo(() => getTaskStepDueDate(step), [step]);
  const showBadges = !taskType || !settlementTaskTypes.has(taskType);
  const hasHelperContent =
    Boolean(helperText) ||
    (showBadges && Boolean(badgeMessage)) ||
    (showBadges && Boolean(stepDueDate));

  const disabled = allStepsCompleted || isSaving;
  const toggleStep = () => {
    if (!disabled) onToggleStep(step);
  };

  const onCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    // The checkbox/label and any links handle their own clicks.
    if ((event.target as HTMLElement).closest('input, label, a, button')) return;

    toggleStep();
  };

  return (
    <div
      className={clsx('taskStep-container', 'taskStep-item', {
        disabled,
      })}
      onClick={onCardClick}
    >
      <AgxCheckbox
        id={`${step.order}-${step.name}`}
        checked={!!step.isCompleted}
        onChange={toggleStep}
        strikethroughOnCheck
        label={labelText}
        disabled={disabled}
      />
      {hasHelperContent && (
        <div className="taskStep-helperContent">
          <AgxTypography
            id={`task-step-helper-${step.order}`}
            key={`task-step-helper-${step.order}`}
            type={'body_ui_strong'}
            size={'medium'}
            color="var(--text-primary)"
          >
            {helperText}
          </AgxTypography>
          {showBadges && stepDueDate && (
            <div className="taskStep-dueDateBadge">
              <TaskDueDateBadge
                dueDate={stepDueDate}
                isCompleted={step.isCompleted ?? false}
                completedDate={step.completedUtc as string}
              />
            </div>
          )}
          {showBadges && badgeMessage && (
            <div className="taskStep-helperBadge">
              <AgxBadge type="colourfill" colour="grey" size="md" text={badgeMessage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskStepItem;
